<template>
    <div>
        <div class="main-container">
            <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

            <!-- Back nav -->
            <div class="req-back">
                <a class="req-back__link" href="javascript:void(0)"
                    @click="$router.push({ path: '/kids-device', query: { profileId: profileId } })">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Home
                </a>
            </div>

            <!-- Page header -->
            <div class="bl-page-header">
                <div :style="{ width: '100%' }">
                    <h1 class="bl-page-header__title">Online Safety</h1>
                    <p class="bl-page-header__sub">Chrome browsing activity & site controls</p>
                </div>
            </div>

            <!-- Tabs -->
            <div class="bl-tabs">
                <button class="bl-tab" :class="{ 'bl-tab--active': activeTab === 'popular' }" @click="activeTab = 'popular'">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Popular Sites
                </button>
                <button class="bl-tab" :class="{ 'bl-tab--active': activeTab === 'logs' }" @click="activeTab = 'logs'">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" stroke="currentColor" stroke-width="1.8"/>
                    </svg>
                    All Urls
                </button>
            </div>

            <!-- Default blocklist warning -->
            <div class="bl-default-banner">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="flex-shrink:0;">
                    <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.8"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
                <div class="bl-default-banner__body">
                    <span class="bl-default-banner__title">Some sites are globally protected</span>
                    <span class="bl-default-banner__sub">
                        Sites marked <strong>Protected</strong> are blocked by default for all devices.
                        <a href="javascript:void(0)"
                            @click="$router.push({ path: '/contact-us', query: { profileId: profileId } })"
                            class="bl-default-banner__link">
                            Contact support
                        </a> to request changes.
                    </span>
                </div>
            </div>
            <!-- ══════════════════════════════════════ -->
            <!-- TAB 1: Popular Sites (static for now) -->
            <!-- ══════════════════════════════════════ -->
            <div v-if="activeTab === 'popular'">

                <!-- Info banner -->
                <div class="bl-info-banner">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="flex-shrink:0;">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                        <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    </svg>
                    Quickly block or allow popular sites. Blocked sites will be restricted in Chrome.
                </div>

                <!-- Block all / Unblock all -->
                <div class="bl-bulk-actions">
                    <button class="bl-bulk-btn bl-bulk-btn--block" @click="blockAllPopular()">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M4.93 4.93l14.14 14.14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                        Block All
                    </button>
                    <button class="bl-bulk-btn bl-bulk-btn--allow" @click="unblockAllPopular()">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        Unblock All
                    </button>
                </div>

                <!-- Categories -->
                <div v-for="cat in popularCategories" :key="cat.name">
                    <div class="bl-section-label">
                        {{ cat.name }}
                        <div class="bl-cat-actions">
                            <button class="bl-cat-btn bl-cat-btn--block" @click="blockCategory(cat)">Block all</button>
                            <button class="bl-cat-btn bl-cat-btn--allow" @click="allowCategory(cat)">Unblock all</button>
                        </div>
                    </div>
                    <div class="bl-card">
                        <div class="bl-site-row"
                            v-for="(site, idx) in cat.sites"
                            :key="site.id"
                            :class="{
                                'bl-site-row--blocked': site.blocked,
                                'bl-site-row--default': site.isDefault
                            }">
                            <div class="bl-site-row__icon">
                                <span v-html="site.icon_svg" class="bl-site-row__svg"></span>
                            </div>
                            <div class="bl-site-row__body">
                                <span class="bl-site-row__name">{{ site.name }}</span>
                                <span class="bl-site-row__url">{{ site.url }}</span>
                            </div>

                            <!-- Default blocked badge -->
                            <span class="bl-default-badge" v-if="site.isDefault"
                                :title="'This site is globally blocked. Contact support to unblock.'">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                                    <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="2"/>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                </svg>
                                Protected
                            </span>
                            <span class="bl-blocked-badge" v-else-if="site.blocked">Blocked</span>

                            <!-- Toggle — disabled for default sites -->
                            <label class="kid-action-switch" :class="{ 'kid-action-switch--disabled': site.isDefault }" @click.stop>
                                <input
                                    type="checkbox"
                                    :checked="site.blocked"
                                    :disabled="site.isDefault"
                                    @change="togglePopularSite(site)"
                                />
                                <span class="kid-action-switch__track"></span>
                            </label>

                            <div class="bl-site-row__divider" v-if="idx < cat.sites.length - 1"></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ══════════════════════════════════════ -->
            <!-- TAB 2: Browser Logs (existing code)   -->
            <!-- ══════════════════════════════════════ -->
            <div v-if="activeTab === 'logs'">

                <!-- Add URL card -->
                <div class="bl-section-label">Add URL</div>
                <div class="bl-card">
                    <div class="bl-add-row">
                        <div class="bl-add-row__input-wrap">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="flex-shrink:0; color:#9ca3af;">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" stroke="currentColor" stroke-width="1.8"/>
                            </svg>
                            <input
                                type="text"
                                class="bl-add-row__input"
                                placeholder="Enter URL e.g. youtube.com"
                                v-model="url"
                            />
                        </div>
                        <select class="bl-add-row__select" v-model="isBlocked" aria-label="Status">
                            <option value="">Status</option>
                            <option value="true">Block</option>
                            <option value="false">Unblock</option>
                        </select>
                        <button class="bl-add-btn" @click="addUrl()" :disabled="isDisabled">
                            <span v-if="isDisabled"><i class="fa fa-spinner fa-spin"></i></span>
                            <span v-else>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                </svg>
                                Add
                            </span>
                        </button>
                    </div>
                </div>

                <!-- Date navigator -->
                <div class="bl-section-label">Browse History</div>
                <!--<div class="bl-date-nav">
                    <button class="bl-date-nav__btn" @click="previousDate()" aria-label="Previous">
                        <ArrowLeftIcon />
                    </button>
                    <span class="bl-date-nav__label">
                        {{ userSelectedDate ? formattedDate : 'All logs' }}
                    </span>
                    <button class="bl-date-nav__btn" @click="nextDate()" aria-label="Next">
                        <ArrowRightIcon />
                    </button>
                    <div class="bl-date-nav__cal date-picker-wrapper">
                        <label for="browserLogsDatePicker" class="date-picker-label" aria-label="Pick date">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                                <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </label>
                        <input
                            id="browserLogsDatePicker"
                            ref="datePicker"
                            type="date"
                            v-model="selectedDate"
                            @change="onDateChange"
                            class="date-picker-input"
                        />
                    </div>
                </div>-->

                <!-- Unblock all -->
                <div class="bl-actions-row" v-if="filterByStatus == 'block' && browserLogsData.length > 0">
                    <button class="bl-unblock-all-btn" @click="unBlockAll()" :disabled="isDisabled">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M7 11V7a5 5 0 0 1 9.9-1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                        <span v-if="isDisabled"><i class="fa fa-spinner fa-spin"></i> Unblocking...</span>
                        <span v-else>Unblock All</span>
                    </button>
                </div>

                <!-- Status filter pills -->
                <div class="bl-filter-pills">
                    <button class="bl-pill" :class="{ 'bl-pill--active': filterByStatus === '' }" @click="setStatusFilter('')">
                        All
                    </button>
                    <button class="bl-pill bl-pill--block" :class="{ 'bl-pill--active': filterByStatus === 'block' }" @click="setStatusFilter('block')">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                        Blocked
                        <span class="bl-pill__count" v-if="blockedCount > 0">{{ blockedCount }}</span>
                    </button>
                    <button class="bl-pill bl-pill--allow" :class="{ 'bl-pill--active': filterByStatus === 'unblock' }" @click="setStatusFilter('unblock')">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M7 11V7a5 5 0 0 1 9.9-1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                        Allowed
                        <span class="bl-pill__count" v-if="allowedCount > 0">{{ allowedCount }}</span>
                    </button>
                </div>

                <!-- Loading -->
                <div class="bl-loader" v-if="isDisabled && browserLogsData.length === 0">
                    <i class="fa fa-spinner fa-spin"></i>
                    <span>Loading browser logs...</span>
                </div>

                <!-- Empty -->
                <div class="bl-empty" v-else-if="browserLogsData.length === 0">
                    <div class="bl-empty__icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="#d1d5db" stroke-width="1.5"/>
                            <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" stroke="#d1d5db" stroke-width="1.5"/>
                        </svg>
                    </div>
                    <span class="bl-empty__title">No logs found</span>
                    <span class="bl-empty__sub">No browsing activity recorded for this date</span>
                </div>

                <!-- Logs list -->
                <div class="bl-card" v-else>
                    <div class="bl-log-item"
                        v-for="(log, idx) in browserLogsData"
                        :key="log._id"
                        :class="{ 'bl-log-item--blocked': log.isBlocked }">
                        <div class="bl-log-item__status"
                            :class="log.isBlocked ? 'bl-log-item__status--blocked' : 'bl-log-item__status--allowed'">
                            <svg v-if="log.isBlocked" width="13" height="13" viewBox="0 0 24 24" fill="none">
                                <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.8"/>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                            </svg>
                            <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none">
                                <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.8"/>
                                <path d="M7 11V7a5 5 0 0 1 9.9-1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                            </svg>
                        </div>
                        <div class="bl-log-item__body">
                            <a :href="addHttp(log.url)" target="_blank" class="bl-log-item__url">{{ log.url }}</a>
                            <div class="bl-log-item__meta">
                                <span>Updated: {{ formatDate(log.updated_at) }}</span>
                                <span class="bl-log-item__dot">·</span>
                                <span>Created: {{ formatDate(log.created_at) }}</span>
                            </div>
                        </div>
                        <button
                            class="bl-log-item__toggle"
                            :class="log.isBlocked ? 'bl-log-item__toggle--blocked' : 'bl-log-item__toggle--allowed'"
                            @click="updateLogUrlStatus(log._id, !log.isBlocked)">
                            <svg v-if="log.isBlocked" width="13" height="13" viewBox="0 0 24 24" fill="none">
                                <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.8"/>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                            </svg>
                            <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none">
                                <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.8"/>
                                <path d="M7 11V7a5 5 0 0 1 9.9-1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                            </svg>
                            {{ log.isBlocked ? 'Unblock' : 'Block' }}
                        </button>
                        <div class="bl-log-item__divider" v-if="idx < browserLogsData.length - 1"></div>
                    </div>
                </div>

                <div class="last-updated" style="padding: 16px 0 24px;" v-if="browserLogsData.length > 0">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                        <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    </svg>
                    {{ browserLogsData.length }} entries for {{ formattedDate }}
                </div>
            </div>

            <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
        </div>
    </div>
</template>

<script>
import KidHeader from '@/components/KidHeader.vue'
import KidFooter from '@/components/KidFooter.vue'
import KidsService from "../../services/KidsService";
import ArrowLeftIcon from '@/components/icons/ArrowLeftIcon.vue'
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue'
import { buildPopularCategories } from '@/data/popularServices.js';
import { DEFAULT_BLOCKED_DOMAINS, isDefaultBlocked, DEFAULT_BLOCKED_LABEL } from '@/data/defaultBlocklist.js';

let options = { weekday: 'short', month: 'long', day: 'numeric' };

export default {
  name: 'Browser Logs',
  components: {
    KidHeader,
    KidFooter,
    ArrowLeftIcon,
    ArrowRightIcon,
  },
  data() {
    return {
        // ── existing data unchanged ──
        isDisabled: false,
        profileId: this.$route.query.profileId || this.$route.query.profile_id,
        serialNumber: this.$route.query.serialNumber,
        formattedDate: new Date().toLocaleDateString("en-US", options),
        currentDate: new Date(),
        filterType: "Day",
        browserLogsData: [],
        urlText: "",
        filterByStatus: "",
        url: "",
        isBlocked: "",
        selectedDate: '',

        // ── new: tab state ──
        activeTab: 'popular',
        userSelectedDate: false,

        // ── new: static popular categories ──
        popularCategories: buildPopularCategories(),
        allBrowserLogsData: [],
        DEFAULT_BLOCKED_DOMAINS,
        isDefaultBlocked,
    }
  },
  computed: {
        blockedCount() {
            return this.browserLogsData.filter(l => l.isBlocked).length;
        },
        allowedCount() {
            return this.browserLogsData.filter(l => !l.isBlocked).length;
        },
    },
  methods: {
    async getAllLogsForPopularSync() {
        if (this._syncingPopular) return; // prevent concurrent calls
        this._syncingPopular = true;
        try {
            const response = await KidsService.getBrowserLogs({
                serialNumber: this.serialNumber,
            });
            this.allBrowserLogsData = response.data.data || [];
            this.syncPopularSiteStates();
        } catch (e) {
            console.error('Failed to sync popular sites', e);
        } finally {
            this._syncingPopular = false;
        }
    },
    setStatusFilter(status) {
        this.filterByStatus = status;
        this.getBrowserLogs();
    },
    // ── ALL EXISTING METHODS UNCHANGED ──
    onDateChange() {
        if (this.selectedDate) {
            const parts = this.selectedDate.split('-');
            const year = parseInt(parts[0]);
            const month = parseInt(parts[1]) - 1;
            const day = parseInt(parts[2]);
            this.currentDate = new Date(year, month, day);
            this.formattedDate = this.currentDate.toLocaleDateString("en-US", options);
            this.userSelectedDate = true; // ← user explicitly picked
            this.getBrowserLogs();
        }
    },
    formatDateForInput(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    },
    previousDate() {
        if (this.filterType == "Day") {
            let day = this.currentDate.getTime() - (1 * 24 * 60 * 60 * 1000);
            this.currentDate.setTime(day);
            this.formattedDate = this.currentDate.toLocaleDateString("en-US", options);
            this.selectedDate = this.formatDateForInput(this.currentDate);
            this.userSelectedDate = true; // ← user navigated
        } else {
            this.formattedDate = "Previous Week";
            this.weekType = "Previous";
        }
        this.getBrowserLogs();
    },
    nextDate() {
        if (this.filterType == "Day") {
            let day = this.currentDate.getTime() + (1 * 24 * 60 * 60 * 1000);
            this.currentDate.setTime(day);
            this.formattedDate = this.currentDate.toLocaleDateString("en-US", options);
            this.selectedDate = this.formatDateForInput(this.currentDate);
            this.userSelectedDate = true; // ← user navigated
        } else {
            this.formattedDate = "This Week";
            this.weekType = "Current";
        }
        this.getBrowserLogs();
    },
    async getBrowserLogs() {
        this.isDisabled = true;
        let data = {
            serialNumber: this.serialNumber,
            urlText: this.urlText,
            filterByStatus: this.filterByStatus,
            // Only send date if user explicitly selected one
            ...(this.userSelectedDate && { currentDate: this.currentDate }),
        };
        await KidsService.getBrowserLogs(data)
        .then((response) => {
            this.isDisabled = false;
            this.browserLogsData = response.data.data;
            this.syncPopularSiteStates();
        }).catch((e) => {
            this.isDisabled = false;
            this.$toast.error(e.response.data.respones_msg);
        });
    },
    // Add this helper method
    matchesRule(logUrl, rule) {
        if (!logUrl || !rule) return false;
        const cleanLog = logUrl.toLowerCase()
            .replace(/^https?:\/\//, '')
            .replace(/^www\./, '')
            .replace(/\/.*$/, '')
            .trim();
        const cleanRule = rule.toLowerCase()
            .replace(/^www\./, '')
            .trim();
        return cleanLog === cleanRule || cleanLog.endsWith('.' + cleanRule);
    },
    prepareBulkPayload(sites, newState) {
        const existingIds = [];
        const newUrls = [];

        sites.forEach(site => {
            if (site.isDefault) return; // skip default protected sites

            site.rules.forEach(rule => {
                const existing = this.allBrowserLogsData.find(log =>
                    log.serialNumber === this.serialNumber &&
                    this.matchesRule(log.url, rule)
                );
                if (existing) {
                    existingIds.push(existing._id);
                    existing.isBlocked = newState; // update local cache
                } else {
                    newUrls.push(rule);
                }
            });
        });

        return { existingIds, newUrls };
    },
    // Add this computed or call it when activeTab switches to 'popular'
    syncPopularSiteStates() {
        this.popularCategories = this.popularCategories.map(cat => ({
            ...cat,
            sites: cat.sites.map(site => {
                const isDefault = site.rules.some(rule => isDefaultBlocked(rule));

                if (isDefault) {
                    return { ...site, blocked: true, isDefault: true, _id: null, _dbIds: [] };
                }

                const matchedLogs = this.allBrowserLogsData.filter(log =>
                    log.serialNumber === this.serialNumber &&
                    log.url && site.rules.some(rule => this.matchesRule(log.url, rule))
                );

                if (matchedLogs.length > 0) {
                    return {
                        ...site,
                        isDefault: false,
                        blocked: matchedLogs.some(l => l.isBlocked),
                        _dbIds: matchedLogs.map(l => l._id),
                        _id: matchedLogs[0]._id,
                    };
                }

                return { ...site, isDefault: false, blocked: false, _id: null, _dbIds: [] };
            })
        }));
    },
    async unBlockAll() {
        this.isDisabled = true;
        let data = { serialNumber: this.serialNumber };
        await KidsService.unBlockAll(data)
        .then((response) => {
            this.isDisabled = false;
            this.getBrowserLogs();
        }).catch((e) => {
            this.isDisabled = false;
            this.$toast.error(e.response.data.respones_msg);
        });
    },
    async updateLogUrlStatus(id, isBlocked) {
        const log = this.browserLogsData.find(l =>
            l._id === id &&
            l.serialNumber === this.serialNumber
        );
        if (log && isDefaultBlocked(log.url) && !isBlocked) {
            this.$toast.error('This site is in the default protected list. Contact support to unblock.');
            return;
        }
        let data = { id: id, isBlocked: isBlocked, serialNumber: this.serialNumber };
        await KidsService.updateLogUrlStatus(data)
        .then((response) => {
            this.getBrowserLogs();
        }).catch((e) => {
            this.$toast.error(e.response.data.respones_msg);
        });
    },
    formatDate(dateStr) {
        const date = new Date(dateStr);
        return date.toLocaleString('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short'
        });
    },
    async addUrl() {
        this.isDisabled = true;
        let data = { serialNumber: this.serialNumber, url: this.url, isBlocked: this.isBlocked };
        await KidsService.createBrowserLog(data)
        .then((response) => {
            this.isDisabled = false;
            this.getBrowserLogs();
        }).catch((e) => {
            this.isDisabled = false;
            this.$toast.error(e.response.data.respones_msg);
        });
    },
    addHttp(url) {
        if (!/^https?:\/\//i.test(url)) {
            return 'http://' + url;
        }
        return url;
    },

    async togglePopularSite(site) {
        if (site.isDefault) {
            this.$toast.error('This site is in the default protected list. Contact support to unblock.');
            return;
        }

        const newState = !site.blocked;

        // Optimistic update
        const catIndex = this.popularCategories.findIndex(c => c.sites.some(s => s.id === site.id));
        if (catIndex > -1) {
            const siteIndex = this.popularCategories[catIndex].sites.findIndex(s => s.id === site.id);
            if (siteIndex > -1) {
                this.popularCategories[catIndex].sites[siteIndex] = {
                    ...this.popularCategories[catIndex].sites[siteIndex],
                    blocked: newState
                };
            }
        }

        try {
            // Split rules into existing (update) and new (create)
            const existingIds = [];
            const newUrls = [];

            for (const rule of site.rules) {
                const existing = this.allBrowserLogsData.find(log =>
                    log.serialNumber === this.serialNumber &&
                    this.matchesRule(log.url, rule)
                );
                if (existing) {
                    existingIds.push(existing._id);
                    existing.isBlocked = newState; // update local cache
                } else {
                    newUrls.push(rule);
                }
            }

            // Single update call with array of ids
            if (existingIds.length > 0) {
                await KidsService.updateLogUrlStatus({
                    id: existingIds,          // array
                    isBlocked: newState,
                    serialNumber: this.serialNumber
                });
            }

            // Single create call with array of urls
            if (newUrls.length > 0) {
                await KidsService.createBrowserLog({
                    serialNumber: this.serialNumber,
                    url: newUrls,             // array
                    isBlocked: newState ? 'true' : 'false'
                });
            }

            // Single sync refresh after both calls
            await this.getAllLogsForPopularSync();

        } catch (e) {
            // Revert optimistic update
            if (catIndex > -1) {
                const siteIndex = this.popularCategories[catIndex].sites.findIndex(s => s.id === site.id);
                if (siteIndex > -1) {
                    this.popularCategories[catIndex].sites[siteIndex] = {
                        ...this.popularCategories[catIndex].sites[siteIndex],
                        blocked: !newState
                    };
                }
            }
            this.$toast.error(e?.response?.data?.respones_msg || 'Failed to update');
        }
    },
    async blockCategory(cat) {
        await this.bulkToggleCategory(cat, true);
    },

    async allowCategory(cat) {
        await this.bulkToggleCategory(cat, false);
    },

    async blockAllPopular() {
        const allSites = this.popularCategories.flatMap(cat => cat.sites);
        await this.bulkToggleSites(allSites, true);
    },

    async unblockAllPopular() {
        const allSites = this.popularCategories.flatMap(cat => cat.sites);
        await this.bulkToggleSites(allSites, false);
    },
    async bulkToggleCategory(cat, newState) {
        await this.bulkToggleSites(cat.sites, newState);
    },
    async bulkToggleSites(sites, newState) {
        // Optimistic update
        this.popularCategories = this.popularCategories.map(cat => ({
            ...cat,
            sites: cat.sites.map(site => {
                if (site.isDefault) return site;
                if (!sites.find(s => s.id === site.id)) return site;
                return { ...site, blocked: newState };
            })
        }));

        const { existingIds, newUrls } = this.prepareBulkPayload(sites, newState);

        if (existingIds.length === 0 && newUrls.length === 0) return;

        try {
            await KidsService.bulkUpdateBrowserLogs({
                serialNumber: this.serialNumber,
                isBlocked: newState,
                existingIds,
                newUrls
            });
            // Single sync refresh after entire bulk operation
            await this.getAllLogsForPopularSync();
        } catch (e) {
            // Revert optimistic update on error
            await this.getAllLogsForPopularSync();
            this.$toast.error(e?.response?.data?.respones_msg || 'Bulk update failed');
        }
    },
  },
  beforeMount() {
        this.selectedDate = this.formatDateForInput(this.currentDate);
        this.userSelectedDate = false;
        this.getBrowserLogs();
        this.getAllLogsForPopularSync(); // ← fetch all for popular sync
    },
}
</script>

<style scoped>
/* ── Back nav ── */
.req-back { padding: 12px 14px 8px; width: 100%; text-align: left; }
.req-back__link { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: #374151; text-decoration: none; -webkit-tap-highlight-color: transparent; }

/* ── Page header ── */
.bl-page-header { width: 100%; display: flex; align-items: center; justify-content: space-between; padding: 4px 14px 10px; }
.bl-page-header__title { font-size: 20px; font-weight: 700; color: #111; margin: 0 0 2px; }
.bl-page-header__sub { font-size: 12px; color: #6b7280; margin: 0; }

/* ── Tabs ── */
.bl-tabs {
    width: 100%;
    display: flex; gap: 0;
    margin: 0 14px 12px;
    background: #f3f4f6; border-radius: 12px; padding: 3px;
}
.bl-tab {
    flex: 1; display: inline-flex; align-items: center; justify-content: center;
    gap: 5px; height: 36px; border: none; background: none;
    font-size: 12px; font-weight: 600; color: #6b7280;
    border-radius: 10px; cursor: pointer;
    -webkit-tap-highlight-color: transparent; transition: all 0.15s;
}
.bl-tab--active { background: #fff; color: #111; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }

/* ── Info banner ── */
.bl-info-banner {
    display: flex; align-items: flex-start; gap: 8px;
    margin: 0 14px 10px; padding: 10px 12px;
    background: #eff6ff; border: 0.5px solid #bfdbfe;
    border-radius: 10px; font-size: 12px; color: #1d4ed8; line-height: 1.4;
}

/* ── Bulk actions ── */
.bl-bulk-actions { display: flex; gap: 8px; padding: 0 14px 8px; }
.bl-bulk-btn {
    flex: 1; height: 38px; border-radius: 10px;
    display: inline-flex; align-items: center; justify-content: center;
    gap: 6px; font-size: 13px; font-weight: 600;
    cursor: pointer; border: 0.5px solid transparent;
    -webkit-tap-highlight-color: transparent;
}
.bl-bulk-btn--block { background: #fff5f5; border-color: #fecaca; color: #dc2626; }
.bl-bulk-btn--allow { background: #f0fdf4; border-color: #bbf7d0; color: #16a34a; }
.bl-bulk-btn--block:active { background: #fee2e2; }
.bl-bulk-btn--allow:active { background: #dcfce7; }

/* ── Section label ── */
.bl-section-label {
    display: flex; align-items: center; justify-content: space-between;
    font-size: 11px; font-weight: 700; color: #6b7280;
    text-transform: uppercase; letter-spacing: 0.6px;
    padding: 10px 14px 5px;
}
.bl-cat-actions { display: flex; gap: 10px; }
.bl-cat-btn { font-size: 11px; font-weight: 600; border: none; background: none; cursor: pointer; text-transform: none; letter-spacing: 0; padding: 0; }
.bl-cat-btn--block { color: #dc2626; }
.bl-cat-btn--allow { color: #16a34a; }

/* ── Card ── */
.bl-card { background: #fff; border-radius: 14px; border: 0.5px solid #e5e7eb; margin: 0 14px 10px; overflow: hidden; }

/* ── Popular site row ── */
.bl-site-row {
    display: flex; align-items: center; gap: 10px;
    padding: 11px 14px; position: relative; transition: background 0.12s;
}
.bl-site-row--blocked { background: #fff5f5; }
.bl-site-row__divider { position: absolute; bottom: 0; left: 50px; right: 0; height: 0.5px; background: #f3f4f6; }
.bl-site-row__icon {
    width: 28px; height: 28px; border-radius: 7px;
    background: #f3f4f6; display: flex; align-items: center;
    justify-content: center; overflow: hidden; flex-shrink: 0; color: #9ca3af;
}
.bl-site-row__icon img { width: 20px; height: 20px; object-fit: contain; }
.bl-site-row__body { text-align: left; flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.bl-site-row__name { font-size: 13px; font-weight: 600; color: #111; }
.bl-site-row__url { font-size: 11px; color: #9ca3af; }
.bl-blocked-badge { font-size: 10px; font-weight: 600; background: #fee2e2; color: #dc2626; padding: 2px 7px; border-radius: 20px; flex-shrink: 0; }

/* ── Switch (red when blocked) ── */
.kid-action-switch { position: relative; display: inline-flex; width: 38px; height: 20px; cursor: pointer; margin: 0; flex-shrink: 0; }
.kid-action-switch input { opacity: 0; width: 0; height: 0; position: absolute; }
.kid-action-switch__track { position: absolute; inset: 0; background: #d1d5db; border-radius: 999px; transition: background 0.2s; }
.kid-action-switch__track::after { content: ''; position: absolute; top: 3px; left: 3px; width: 14px; height: 14px; background: white; border-radius: 50%; transition: transform 0.2s; }
.kid-action-switch input:checked + .kid-action-switch__track { background: #dc2626; }
.kid-action-switch input:checked + .kid-action-switch__track::after { transform: translateX(18px); }

/* ── Add row ── */
.bl-add-row { display: flex; align-items: center; gap: 8px; padding: 12px 14px; flex-wrap: wrap; }
.bl-add-row__input-wrap {
    flex: 1; min-width: 0; display: flex; align-items: center; gap: 8px;
    border: 0.5px solid #e5e7eb; border-radius: 10px;
    padding: 0 10px; background: #fafafa; height: 40px;
}
.bl-add-row__input { flex: 1; border: none; background: none; font-size: 13px; color: #111; outline: none; min-width: 0; }
.bl-add-row__input::placeholder { color: #9ca3af; }
.bl-add-row__select {
    height: 40px; padding: 0 28px 0 10px; border-radius: 10px;
    border: 0.5px solid #e5e7eb; background: #fafafa;
    font-size: 13px; color: #374151; outline: none;
    flex-shrink: 0; -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg width='10' height='10' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 9l6 6 6-6' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right 8px center;
}
.bl-add-btn {
    height: 40px; padding: 0 14px; border-radius: 10px; border: none;
    background: #2563eb; color: #fff; font-size: 13px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center; gap: 5px;
    flex-shrink: 0; -webkit-tap-highlight-color: transparent;
}
.bl-add-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Date navigator ── */
.bl-date-nav {
    display: flex; align-items: center; gap: 4px;
    margin: 0 14px 10px; background: #fff;
    border: 0.5px solid #e5e7eb; border-radius: 12px; padding: 4px 6px;
}
.bl-date-nav__btn {
    width: 36px; height: 36px; border-radius: 8px; border: none;
    background: none; color: #374151;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; flex-shrink: 0; -webkit-tap-highlight-color: transparent;
}
.bl-date-nav__btn:active { background: #f3f4f6; }
.bl-date-nav__label { flex: 1; text-align: center; font-size: 13px; font-weight: 600; color: #111; }
.bl-date-nav__cal { width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; color: #374151; flex-shrink: 0; }

/* ── Date picker ── */
.date-picker-wrapper { position: relative; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.date-picker-label { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; margin: 0; cursor: pointer; z-index: 1; position: relative; }
.date-picker-input { position: absolute; top: 0; left: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; z-index: 10; }
.date-picker-input::-webkit-calendar-picker-indicator { position: absolute; top: 0; left: 0; right: 0; bottom: 0; width: 100%; height: 100%; margin: 0; padding: 0; opacity: 0; cursor: pointer; }
@media (max-width: 768px) { .date-picker-input { font-size: 16px; } }

/* ── Unblock all ── */
.bl-actions-row { padding: 0 14px 8px; display: flex; justify-content: flex-end; }
.bl-unblock-all-btn {
    display: inline-flex; align-items: center; gap: 6px;
    height: 36px; padding: 0 14px; border-radius: 10px;
    border: 0.5px solid #fde68a; background: #fffbeb;
    font-size: 12px; font-weight: 600; color: #92400e; cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}
.bl-unblock-all-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Loader / Empty ── */
.bl-loader { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 48px 14px; color: #6b7280; font-size: 13px; }
.bl-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 48px 14px; text-align: center; }
.bl-empty__icon { width: 72px; height: 72px; border-radius: 50%; background: #f3f4f6; display: flex; align-items: center; justify-content: center; margin-bottom: 8px; }
.bl-empty__title { font-size: 16px; font-weight: 700; color: #111; }
.bl-empty__sub { font-size: 13px; color: #6b7280; }

/* ── Log items ── */
.bl-log-item { display: flex; align-items: center; gap: 10px; padding: 12px 14px; position: relative; transition: background 0.12s; }
.bl-log-item--blocked { background: #fff5f5; }
.bl-log-item__divider { position: absolute; bottom: 0; left: 48px; right: 0; height: 0.5px; background: #f3f4f6; }
.bl-log-item__status { width: 28px; height: 28px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.bl-log-item__status--blocked { background: #fee2e2; color: #dc2626; }
.bl-log-item__status--allowed { background: #dcfce7; color: #16a34a; }
.bl-log-item__body { text-align: left; flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.bl-log-item__url { font-size: 13px; font-weight: 500; color: #2563eb; text-decoration: none; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bl-log-item__meta { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; font-size: 11px; color: #9ca3af; }
.bl-log-item__dot { color: #d1d5db; }
.bl-log-item__toggle { display: inline-flex; align-items: center; gap: 4px; height: 30px; padding: 0 10px; border-radius: 8px; font-size: 11px; font-weight: 600; border: none; cursor: pointer; flex-shrink: 0; -webkit-tap-highlight-color: transparent; }
.bl-log-item__toggle--blocked { background: #fff5f5; color: #dc2626; }
.bl-log-item__toggle--allowed { background: #f9fafb; color: #374151; }
.bl-log-item__toggle:active { opacity: 0.75; }

/* ── Last updated ── */
.last-updated { display: flex; align-items: center; justify-content: center; gap: 5px; font-size: 12px; color: #9ca3af; }

button:focus { outline: none; }

/* ── Status filter pills ── */
.bl-filter-pills {
    display: flex; gap: 6px;
    padding: 0 14px 10px;
    overflow-x: auto; scrollbar-width: none;
}
.bl-filter-pills::-webkit-scrollbar { display: none; }

.bl-pill {
    display: inline-flex; align-items: center; gap: 5px;
    height: 32px; padding: 0 12px;
    border-radius: 20px; border: 0.5px solid #e5e7eb;
    background: #f9fafb; color: #374151;
    font-size: 12px; font-weight: 600;
    cursor: pointer; white-space: nowrap; flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
    transition: all 0.12s;
}
.bl-pill:active { opacity: 0.8; }

/* Active state — All */
.bl-pill--active {
    background: #2563eb;
    border-color: #2563eb;
    color: #fff;
}

/* Block pill */
.bl-pill--block.bl-pill--active {
    background: #dc2626;
    border-color: #dc2626;
    color: #fff;
}

/* Allow pill */
.bl-pill--allow.bl-pill--active {
    background: #16a34a;
    border-color: #16a34a;
    color: #fff;
}

.bl-pill__count {
    display: inline-flex; align-items: center; justify-content: center;
    min-width: 16px; height: 16px; padding: 0 4px;
    border-radius: 20px; font-size: 10px; font-weight: 700;
    background: rgba(255,255,255,0.25);
}

/* When not active, count uses colored bg */
.bl-pill--block:not(.bl-pill--active) .bl-pill__count { background: #fee2e2; color: #dc2626; }
.bl-pill--allow:not(.bl-pill--active) .bl-pill__count { background: #dcfce7; color: #16a34a; }
.bl-pill:not(.bl-pill--active) { }
/* ── Default protected row ── */
.bl-site-row--default {
    background: #f9fafb;
    opacity: 0.85;
}

.bl-default-badge {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 10px; font-weight: 700;
    background: #f3f4f6; color: #374151;
    padding: 2px 7px; border-radius: 20px;
    border: 0.5px solid #e5e7eb;
    flex-shrink: 0; white-space: nowrap;
}

/* Disabled switch */
.kid-action-switch--disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
}

/* SVG icon from services.json */
.bl-site-row__svg {
    width: 20px; height: 20px;
    display: flex; align-items: center; justify-content: center;
}
.bl-site-row__svg svg {
    width: 20px; height: 20px;
    color: #374151;
}

/* ── Default blocked banner ── */
.bl-default-banner {
    display: flex; align-items: flex-start; gap: 10px;
    margin: 0 14px 10px; padding: 12px 14px;
    background: #fffbeb; border: 0.5px solid #fde68a;
    border-radius: 12px; color: #92400e;
}
.bl-default-banner__body { display: flex; flex-direction: column; gap: 3px; }
.bl-default-banner__title { font-size: 13px; font-weight: 700; color: #92400e; }
.bl-default-banner__sub { font-size: 12px; color: #b45309; line-height: 1.4; }
.bl-default-banner__link { color: #b45309; font-weight: 600; text-decoration: underline; }
</style>